import {html, nothing, type HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {get, translate} from "lit-translate";

import {AbstractO2dElement} from "@o2d/shared";
import {loadEmployeePool, type PoolEntry} from "../../../domain/gimmicks/employee-pool";
import type {SkinOf} from "../../../domain/gimmicks/gimmick-registry";
import {randomIndex} from "../../../domain/gimmicks/random-index";
import {prefersReducedMotion} from "../../../helpers/prefers-reduced-motion.helper";
import {startFrameTween, type FrameTween} from "../frame-tween";
import {GimmickSkinController} from "../gimmick-skin.controller";
import {renderCardsSkin} from "./skins/cards.skin";
import {renderDrumSkin} from "./skins/drum.skin";
import type {PickerPhase, PickerSkinInput} from "./skins/picker-skin-input";
import {renderSlotSkin} from "./skins/slot.skin";
import {renderWheelSkin} from "./skins/wheel.skin";

const SKIN_RENDERERS: Readonly<Record<SkinOf<'picker'>, (input: PickerSkinInput) => HTMLTemplateResult>> = {
    cards: renderCardsSkin,
    slot: renderSlotSkin,
    drum: renderDrumSkin,
    wheel: renderWheelSkin,
};

/** How long a draw moves, in milliseconds (0 under reduced motion). */
export const DRAW_DURATION = 2500;

export type PoolStatus = 'loading' | 'ready' | 'empty' | 'error';

/**
 * The picker gimmick (feature plan 0072): draws one of all active employees at random, freshly on every click,
 * in this viewer's browser — nothing is stored or shared, every screen draws for itself. Mounted by
 * `link-tile-gimmick` inside a Linkboard tile.
 *
 * - POOL: loaded once per element, on the first connect (`loading | ready | empty | error`). A load counter
 *   lets only the last started load count, and nothing is set after a disconnect. Connected again, it loads
 *   again only while no pool has arrived; a loaded pool and the last result stay.
 * - DRAW: the handler checks `phase !== 'spinning'` and a loaded pool itself. While a draw moves, the button
 *   is only `aria-disabled` — a real `disabled` would throw the keyboard focus off the button just pressed;
 *   it is `disabled` only while there is no pool. The result is fixed at the click
 *   (`randomIndex`), then the look moves towards it (`frame-tween.ts`, 2.5 s, the base class's one frame slot;
 *   at once under reduced motion). The big name shows the new result only when `shown` — while moving, the
 *   one before — so the animation does not give it away. Disconnected while moving: `shown` at once, so
 *   "Ziehen" never stays locked.
 * - LOOK: `GimmickSkinController`, shared with the stopwatch — it alone follows `tileId` and `savedSkin`
 *   (the pool does not depend on the tile); a new look takes over a running draw.
 * - A11Y: a live region (`aria-live="polite"`), always rendered, gets its text when a result is shown — with
 *   the draw's number, so the same name drawn twice is announced again.
 *
 * A NEW component, so it extends `AbstractO2dElement` (ADR-007; Light DOM); styles in ralphy.css, sections
 * "Gimmicks: gemeinsamer Rahmen" and "Gimmicks: Zufallsauswahl".
 */
@customElement('o2d-gimmick-picker')
export class GimmickPickerComponent extends AbstractO2dElement {

    /** The tile's id — the key of this viewer's look. */
    @property({type: String})
    public tileId = '';

    /** The look stored with the tile, raw (may be one this version does not know). */
    @property({type: String})
    public savedSkin = '';

    @state()
    private _status: PoolStatus = 'loading';

    @state()
    private _phase: PickerPhase = 'idle';

    @state()
    private _progress = 0;

    @state()
    private _current: PoolEntry | null = null;

    @state()
    private _previous: PoolEntry | null = null;

    /** The live region's text — set when a result is shown, never while moving. */
    @state()
    private _announced = '';

    private _pool: readonly PoolEntry[] = [];
    private _drawNumber = 0;
    private _loadCounter = 0;
    private _tween: FrameTween | null = null;
    private readonly _skinController = new GimmickSkinController(this, 'picker');

    public override connectedCallback(): void {
        super.connectedCallback();
        if (this._status === 'loading' || this._status === 'error') {
            void this._load();
        }
    }

    public override disconnectedCallback(): void {
        // A load still running sets nothing any more; the status stays `loading`, so connecting again loads.
        this._loadCounter++;
        if (this._phase === 'spinning') {
            this._finish();
        }
        super.disconnectedCallback(); // cancels the pending frame
    }

    protected render(): HTMLTemplateResult {
        const ready = this._status === 'ready';
        const skin = this._skinController.skin;
        const shownEntry = this._phase === 'spinning' ? this._previous : this._current;
        const input: PickerSkinInput = {
            pool: this._pool,
            current: this._current,
            previous: this._previous,
            progress: this._progress,
            phase: this._phase,
        };

        return html`
            <div class="gimmick picker" data-skin=${skin} data-phase=${this._phase} data-status=${this._status}>
                <div class="gimmick__face">${ready ? SKIN_RENDERERS[skin](input) : nothing}</div>
                <div class="picker__name">${this._nameLine(shownEntry)}</div>
                <div class="gimmick__controls">
                    <button type="button" class="picker__draw"
                            aria-label=${translate('gimmicks.picker.draw')}
                            title=${translate('gimmicks.picker.draw')}
                            ?disabled=${!ready}
                            aria-disabled=${this._phase === 'spinning' ? 'true' : 'false'}
                            @click=${this._onDraw}></button>
                    <button type="button" class="gimmick__next-skin"
                            aria-label=${translate('gimmicks.next-skin')}
                            title=${translate('gimmicks.next-skin')}
                            @click=${this._onNextSkin}></button>
                </div>
                <div class="gimmick__announce" aria-live="polite">${this._announced}</div>
            </div>
        `;
    }

    private _nameLine(entry: PoolEntry | null): string {
        switch (this._status) {
            case 'loading':
                return '';
            case 'empty':
                return get('gimmicks.picker.empty');
            case 'error':
                return get('gimmicks.picker.error');
            case 'ready':
                return entry === null ? get('gimmicks.picker.none-yet') : entry.name;
        }
    }

    private async _load(): Promise<void> {
        const ticket = ++this._loadCounter;
        this._status = 'loading';
        try {
            const pool = await loadEmployeePool();
            if (ticket !== this._loadCounter) {
                return;
            }
            this._pool = pool;
            this._status = pool.length > 0 ? 'ready' : 'empty';
        } catch (error) {
            if (ticket !== this._loadCounter) {
                return;
            }
            console.warn('The employees for the picker could not be loaded', error);
            this._status = 'error';
        }
    }

    private readonly _onDraw = (): void => {
        if (this._phase === 'spinning' || this._status !== 'ready') {
            return;
        }
        this._previous = this._current;
        this._current = this._pool[randomIndex(this._pool.length)];
        this._drawNumber++;
        this._phase = 'spinning';
        const tween = startFrameTween({
            duration: prefersReducedMotion() ? 0 : DRAW_DURATION,
            schedule: (callback) => this.requestFrame(callback),
            now: () => performance.now(),
            onProgress: (progress) => {
                this._progress = progress;
            },
            onDone: () => this._finish(),
        });
        // Under reduced motion the tween has already ended inside `startFrameTween` (and `_finish` set the phase
        // to 'shown' and cleared the field) — keep only a running one. TypeScript still narrows `_phase` to
        // 'spinning' here and calls this check redundant; it is not (review 0072-03).
        this._tween = this._phase === 'spinning' ? tween : null;
    };

    private readonly _onNextSkin = (): void => {
        this._skinController.next();
    };

    /** The draw ends: the result shows and is announced. */
    private _finish(): void {
        this._tween?.cancel();
        this._tween = null;
        this._progress = 1;
        this._phase = 'shown';
        if (this._current !== null) {
            this._announced = get('gimmicks.picker.announce', {name: this._current.name, number: String(this._drawNumber)});
        }
    }
}
